<template>
  <main class="cert-verification-page">
    <div class="cert-verification-page__wrapper ui-container">
      <h1 class="sr-only">
        Страница проверки сертификата
      </h1>

      <div class="cert-verification-page__status">
        <UiPicture
          :data="getUiPictureData({
            sm: [
              'https://cdn.skillbox.pro/wbd-front/academica-static/status/success@1x.avif',
              'https://cdn.skillbox.pro/wbd-front/academica-static/status/success@1x.webp',
              'https://cdn.skillbox.pro/wbd-front/academica-static/status/success@1x.png'
            ]
          })"
          width="88"
          height="88"
          class="cert-verification-page__status-picture"
        />

        <h2 class="cert-verification-page__status-title h h--3">
          Сертификат подтверждён
        </h2>

        <p v-if="computedValidFromDate" class="cert-verification-page__status-description p p--2">
          Действителен с {{ computedValidFromDate }}
        </p>
      </div>

      <div class="cert-verification-page__content">
        <div class="cert-verification-page__card-wrapper">
          <UiCertCard
            title="Скачать сертификат"
            description="Например, чтобы напечатать"
            icon="basic/ui-download"
            icon-label="Скачать"
            class="cert-verification-page__button"
            @click="handleDownloadClick"
          />

          <UiCertCard
            title="Скопировать ссылку"
            description="Можно прикрепить к&nbsp;резюме"
            icon="basic/ui-copy"
            icon-label="Скопировать"
            class="cert-verification-page__button cert-verification-page__button--copy"
            @click="handleCopyLinkClick"
          />
        </div>

        <ul class="cert-verification-page__info-list">
          <li v-if="certificate?.full_name" class="cert-verification-page__info-item f f--2">
            Участник курса

            <span
              class="cert-verification-page__info-value t t--m"
              :class="isXxl ? 't--2' : 't--3'"
            >
              {{ certificate.full_name }}
            </span>
          </li>

          <li v-if="certificate?.course_name" class="cert-verification-page__info-item f f--2">
            Курс

            <span
              class="cert-verification-page__info-value t t--m"
              :class="isXxl ? 't--2' : 't--3'"
              v-html="certificate.course_name"
            />
          </li>

          <li v-if="certificate?.course_duration" class="cert-verification-page__info-item f f--2">
            Длительность в часах

            <span
              class="cert-verification-page__info-value t t--m"
              :class="isXxl ? 't--2' : 't--3'"
            >
              {{ certificate.course_duration }}
            </span>
          </li>
        </ul>

        <div v-if="certificate?.image" class="cert-verification-page__picture-wrapper">
          <UiPicture
            :data="certificate.image"
            width="240"
            height="170"
            mobile-stretch
            class="cert-verification-page__certificate-picture ui-shadow-10pt"
          />
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import { getUiPictureData } from '@academica-box/helpers/getUiPictureData'
  import UiPicture from '@academica-box/components/Media/UiPicture/UiPicture.vue'
  import UiCertCard from '@academica-box/components/Cards/UiCertCard/UiCertCard.vue'
  import { download } from '@academica-box/helpers/files'
  import { useCertificateStore } from '@/store/certificate'

  const viewport = useViewport()
  const route = useRoute()
  const certificateStore = useCertificateStore()
  const { certificate } = storeToRefs(certificateStore)

  const isXxl = computed(() => viewport.isGreaterThan('xxl'))

  const computedValidFromDate = computed(() => {
    if (certificate?.value?.created_at) {
      const date = new Date(certificate.value.created_at)
      const day = date.getDate()
      const month = date.getMonth() + 1 >= 10 ? date.getMonth() + 1 : `0${date.getMonth() + 1}`
      const year = date.getFullYear()

      return `${day}.${month}.${year}`
    }

    return undefined
  })

  useAsyncData(
    'getCertificate',
    () => certificateStore.validate(route.params.id as string),
  )

  useHead({
    title: 'Проверка сертификата | Академика',
  })

  const handleDownloadClick = () => {
    download(certificate?.value?.link || '', 'certificate.png')
  }

  const handleCopyLinkClick = () => {
    navigator.clipboard.writeText(location.href)
  }
</script>

<style lang="scss">
  @use '@academica-box/utils/styles/mixins/UiReset' as *;
  @use '@academica-box/utils/styles/mixins/UiMedia' as *;

  .cert-verification-page {
    padding-top: 48px;
    padding-bottom: 12px;
    background-color: var(--bg-secondary-color);

    @include media(lg) {
      margin: 32px 16px 96px;
      padding: 56px 32px;
      border-radius: 10px;
    }

    @include  media(xl) {
      margin-right: 32px;
      margin-left: 32px;
    }

    @include media(xl, xl-max) {
      padding-right: 96px;
      padding-left: 96px;
    }
  }

  .cert-verification-page__wrapper {
    @include media(lg) {
      display: grid;
      grid-column-gap: 32px;
      grid-template-columns: 288px 1fr;
    }

    @include media(xl) {
      grid-template-columns: 320px 1fr;
    }

    @include media(xxl) {
      grid-template-columns: 464px 1fr;
    }

    @include media(xxl, xxl-max) {
      width: 1312px;
    }
  }

  .cert-verification-page__status {
    @include media(md-max) {
      margin-bottom: 40px;
      text-align: center;
    }

    @include media(lg) {
      grid-row: 1/-1;
    }
  }

  .cert-verification-page__status-picture {
    width: 88px;
    height: 88px;
    margin-bottom: 16px;

    @include media(md-max) {
      margin-right: auto;
      margin-left: auto;
    }

    @include media(lg) {
      width: 112px;
      height: 112px;
      margin-bottom: 32px;
    }
  }

  .cert-verification-page__status-title {
    @include  media(md-max) {
      max-width: 280px;
      margin: 0 auto;
    }

    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }

  .cert-verification-page__status-description {
    margin: 0;
    color: var(--text-secondary-color);
  }

  .cert-verification-page__card-wrapper {
    display: grid;
    grid-row-gap: 6px;
    margin-bottom: 20px;

    @include media(lg) {
      grid-row-gap: 16px;
      margin-bottom: 32px;
    }

    @include media(xl) {
      grid-column-gap: 16px;
      grid-template-columns: 1fr 1fr;
      margin-bottom: 32px;
    }
  }

  .cert-verification-page__info-list {
    @include ulres;

    margin-bottom: 20px;
    padding: 20px;
    border-radius: 10px;
    background-color: var(--bg-main-color);

    @include  media(lg) {
      margin-bottom: 32px;
      padding: 24px;
    }
  }

  .cert-verification-page__info-item {
    position: relative;
    display: flex;
    flex-direction: column;
    padding-bottom: 20px;
    color: var(--text-addition-color);

    &:not(:last-child) {
      margin-bottom: 20px;

      &::after {
        position: absolute;
        bottom: 0;
        width: 100%;
        height: 1px;
        background-color: var(--stroke-main-color);
        content: '';
      }
    }
  }

  .cert-verification-page__info-value {
    max-width: 776px;
    margin-top: 8px;
    color: var(--text-main-color);
  }

  .cert-verification-page__picture-wrapper {
    padding: 20px;
    border-radius: 10px;
    background-color: var(--bg-main-color);

    @include media(md) {
      padding: 36px;
    }

    @include media(lg) {
      padding: 56px;
    }
  }

  .cert-verification-page__certificate-picture {
    margin: 0 auto;

    @include media(sm-max) {
      width: 100%;
      padding-bottom: calc(100% / 4 * 2.83);
    }

    @include media(md) {
      width: 333px;
      height: 236px;
    }

    @include media(lg) {
      width: 496px;
      height: 351px;
    }

    @include media(xl) {
      width: 560px;
      height: 396px;
    }

    @include media(xxl) {
      width: 704px;
      height: 498px;
    }
  }
</style>
